import { GetRowIdParams, RowSelectedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { ChangeEvent, FC, useEffect, useMemo, useRef, useState } from 'react';
import Header from '../../../../common/Header/Header';
import Button from '../../../../components/Button/Button';
import Table from '../../../../components/Table/Table';
import { MENU_FILTERS } from '../../../../constants/ApiEnums';
import {
  useOrganizations,
  fetchMenusInOrganization,
  useMenusInOrg,
  fetchOrganizations,
  useMenusCount,
} from '../../../../services/menu/menu.service';
import { Organization } from '../../../../services/menu/menu.types';
import {
  Buttons,
  FiltersWrapper,
  HeaderInputButton,
  HeaderWrapper,
  MenuExtractPage,
  StyledHeader,
  StyledSubHeader,
  StyledSubTitle,
  TableRowWrapper,
  TableWrapper,
  TreeViewRowWrapper,
} from './SelectMenus.styles';
import { SelectMenusProps } from './SelectMenus.types';
import MenuTreeView from '../../../../components/MenuTreeView/MenuTreeView';
import useTreeViewRowStates from '../../../../components/MenuTreeView/useMenuTreeViewRowStates';
import isEqual from 'react-fast-compare';
import { showToast } from '../../../../components/Toast/Toast';
import { genericErrorShowToastOptions } from '../../../../utils/useGenericErrorMessage/useGenericErrorMessage';
import { ToastTypes } from '../../../../components/Toast/Toast.types';
import { StyledFooter, StyledFooterText, StyledHeaderTitle } from '../../../../components/Table/Table.styles';
import getDataStructure from '../../../../utils/dataStructure/dataStructure';
import Checkbox from '../../../../components/Checkbox/Checkbox';
import Radio from '../../../../components/Radio/Radio';
import InputButton from '../../../../components/InputButton/InputButton';

const MenuExtract: FC<SelectMenusProps> = ({
  activateOrExtract,
  onClick,
  isLoading,
  expandOnCheck = false,
  disableLocalStorage = true,
}) => {
  const { data: orgs, isLoading: isOrgLoading } = useOrganizations({ activateOrExtract }, true);
  const { data: menus } = useMenusInOrg({ activateOrExtract }, true);


  const filterRef = useRef<HTMLInputElement>(null);

  const availableOrgs = useMemo(() => orgs?.data || [], [orgs]);
  const availableMenus = useMemo(() => menus?.data || [], [menus]);
  const selectedGridRef = useRef<AgGridReact>(null);
  const [radioFilter, setRadioFilter] = useState<string>('');
  const [selectedMenus, setSelectedMenus] = useState<Organization[]>([]);
  const [inputError, setInputError] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [searchErrorMessage, setSearchErrorMessage] = useState('');
  const [displayedRowCount, setDisplayedRowCount] = useState<number>(-1);
  const [tableUpdate, setTableUpdate] = useState<boolean>(false);
  const [disableButton, setDisableButton] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>('');
  const [filterId, setFilterId] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  let filter = {[radioFilter]: searchValue}
  const {data} = useMenusCount({activateOrExtract: activateOrExtract.valueOf() }, true);

  if (data && displayedRowCount === -1 && data.status === 206) {
    setDisplayedRowCount(parseInt(data.headers['x-total-count']));
  }

  const translateFilter: any = {
    marketNo: 'Market',
    storeNo: 'Store',
    menuNo: 'Menu',
  };

  const translateForTree: any = {
    marketNo: 'org',
    storeNo: 'store',
    menuNo: 'menu',
  };


  const dataStructure = getDataStructure(activateOrExtract.valueOf());

  const {
    checkedRows,
    expandedRows,
    handleRowClick,
    handleCheckboxClick,
    handleUpwardsDeselectPropagation,
    handleExternalTableDeselectPropagation,
    resetCheckedRows,
  } = useTreeViewRowStates(dataStructure, 'selectMenusTo' + activateOrExtract.valueOf(), disableLocalStorage);

  const handleCheckboxClickInternal = (row: Organization, rowChecked: boolean) => {
    if (rowChecked) {
      if (row.type === 'menu') {
        // menu row was checked
        setSelectedMenus((selectedMenu) => {
          const selectedMenusWithCheckedRowAdded = [...selectedMenu, row];
          const onlyUniqueValues = selectedMenusWithCheckedRowAdded.filter((menu, index) => {
            const firstIndex = selectedMenusWithCheckedRowAdded.findIndex((val) => val.id === menu.id);
            return index === firstIndex;
          });
          return onlyUniqueValues.map((menu) => {
            return {
              ...menu,
            };
          });
        });
      } else {
        if (row.type === 'org') {
          fetchMenusInOrganization(
            { orgId: row.id, activateOrExtract: activateOrExtract.valueOf() },
            (response: { data: any }) => {
              setSelectedMenus((selectedMenus) => {
                const selectedMenusWithResponseDataAdded = [...selectedMenus, ...response.data];
                const onlyUniqueValues = selectedMenusWithResponseDataAdded.filter((menu, index) => {
                  const firstIndex = selectedMenusWithResponseDataAdded.findIndex((val) => val.id === menu.id);
                  return index === firstIndex;
                });

                return onlyUniqueValues.map((org: Organization) => {
                  return {
                    ...org,
                  };
                });
              });
            },
            () => {
              showToast({
                ...genericErrorShowToastOptions,
                label: 'Error fetching menus',
                toastType: ToastTypes.Error,
                id: 'errorFetchingMenusInOrgs',
              });
            }
          );
        } else {
          traverseTree(row);
        }
      }
    } else {
      // row was just unchecked
      setSelectedMenus((selectedMenus) => {
        // filter through selectedMenus and remove all the menus that correspond with that row
        return selectedMenus.filter((menu) => {
          let isRowAndParentEqual;
          if (menu.type === row.type) {
            isRowAndParentEqual = isEqual(row.id, menu.id) && isEqual(row.parents, menu.parents);
          } else {
            isRowAndParentEqual = isEqual(row.id, menu.parents ? menu.parents[row.type] : undefined);
          }
          return !isRowAndParentEqual;
        });
      });
      handleUpwardsDeselectPropagation(row);
    }
    // call the fn from useTreeViewRowStates to mark the row as checked/unchecked there
    handleCheckboxClick(row, rowChecked);
  };

  const traverseTree = (row: Organization) => {
    if (row.type === 'menu') {
      setSelectedMenus((selectedMenu) => {
        const selectedMenusWithCheckedRowAdded = [...selectedMenu, row];
        const onlyUniqueValues = selectedMenusWithCheckedRowAdded.filter((menu, index) => {
          const firstIndex = selectedMenusWithCheckedRowAdded.findIndex((val) => val.id === menu.id);
          return index === firstIndex;
        });
        return onlyUniqueValues.map((menu) => {
          return {
            ...menu,
            rowChecked: true,
          };
        });
      });
      handleCheckboxClick(row, true);
    } else if (row.children !== undefined) {
      row.children.forEach((childRow) => traverseTree(childRow));
    }
  };

  const columnDefs = [
    {
      headerName: 'Select',
      checkboxSelection: true,
      minWidth: 70,
      width: 70,
      floatingFilter: false,
    },
    { headerName: 'Store No', field: 'parents.store', flex: 1, resizable: false },
    { headerName: 'Menu ID', field: 'id' },
  ];

  const getRowId = (params: GetRowIdParams) => {
    return params.data.id;
  };

  // If rowIndex is a number then it means event took place on ag-grid's table
  // If rowIndex is null then we're going to handle everything ourselves
  // Because ag-grid fires an event for every single removal, so un-selecting an org with 200 menus takes a considerate time on old machines
  const handleSelectedMenusChange = (event: RowSelectedEvent) => {
    if (event.node.isSelected() === false && event.rowIndex !== null) {
      const newSelectedMenus = selectedMenus.filter((menu) => menu.id !== event.data.id);
      setSelectedMenus(newSelectedMenus);
      handleCheckboxClick(event.data, false);
      handleExternalTableDeselectPropagation(event.data);
      selectedGridRef.current?.api.selectAll();
      setTableUpdate(!tableUpdate);
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchInputValue(e.target.value);
    setSearchErrorMessage('');
  };

  const menuMap = useMemo(() => {
    const map: { [key: string]: Organization } = {};
    availableMenus.forEach((menu) => {
      map[menu.id] = menu;
    });
    return map;
  }, [availableMenus]);

  const handleAddMenuClick = () => {
    if (searchInputValue && menuMap[searchInputValue]) {
      const menuToAdd = menuMap[searchInputValue];
      setSelectedMenus((prevSelectedMenus) => {
        if (prevSelectedMenus.some((menu) => menu.id === menuToAdd.id)) {
          return prevSelectedMenus;
        }
        return [...prevSelectedMenus, menuToAdd];
      });
      handleCheckboxClick(menuToAdd, true);
    } else {
      setSearchErrorMessage('Menu not found');
    }
  };

  const handleClear = () => {
    setSearchInputValue('');
    setInputError(false);
    setErrorMessage('');
    setSearchErrorMessage('');
    setSearchValue('');
    setFilterId('');
  };

  const handleClick = () => {
    onClick(selectedMenus);
    setDisableButton(true);
  };

  useEffect(() => {
    selectedGridRef?.current?.api?.selectAll();
  }, [selectedMenus, selectedGridRef?.current?.api]);

  const handleSelectAll = () => {
    if (selectedMenus.length === displayedRowCount) {
      resetCheckedRows();
      setSelectedMenus([]);
    } else {
      availableMenus.forEach((menu) => {
        handleCheckboxClick(
          {
            type: 'org',
            id: menu.parents?.org,
            parents: null,
          },
          true
        );
        handleCheckboxClick(menu, true);
        setSelectedMenus(availableMenus);
      });
    }
  };
  const handleRadioValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRadioFilter(event.target.value);
    handleClear();
  };

  const handleSearch = async () => {
    setErrorMessage(undefined);
    setFilterId(() => '');
    let isRowFound = false;
  
    await fetchOrganizations(
      { activateOrExtract: activateOrExtract.valueOf() },
      filter,
      radioFilter,
      (response: { data: Organization[] }) => {
        if (response.data.length > 0) {
          const orgRow = response.data[0];
          switch (radioFilter) {
            case 'marketNo':
              setFilterId(orgRow.id);
              break;
            case 'storeNo':
              setFilterId(searchValue);
              handleRowClick(orgRow, true);
              findAndProcessSearchResult(searchValue, 'store', orgRow, orgRow);
              break;
            case 'menuNo':
              setFilterId(searchValue);
              handleRowClick(orgRow, true);
              findAndProcessSearchResult(searchValue, 'menu', orgRow, orgRow);
              break;
          }
          isRowFound = true;
        }
      }
    );
  
    if (!isRowFound) {
      setErrorMessage(`${translateFilter[radioFilter]} not found`);
    }
  };


  const findAndProcessSearchResult = (searchValue: string, type: string, row: Organization, orgRow: Organization) => {
    if (row.type === type && searchValue === row.id) {
      processSearchResult(row, orgRow);
      return true;
    }
    if (row.children) {
      for (const childRow of row.children) {
        if (findAndProcessSearchResult(searchValue, type, childRow, orgRow)) {
          return true;
        }
      }
    }
    return false;
  };

  const processSearchResult = (targetRow: Organization, orgRow: Organization) => {
    const processSiblings = (row: Organization) => {
      if (row !== targetRow && targetRow.parents && targetRow.parents[row.type] === row.id) {
        handleRowClick(row, true);
      }
      if (row.children) {
        row.children.forEach(processSiblings);
      }
    };
    if (orgRow.children) {
      orgRow.children.forEach(processSiblings);
    }
  };

  const handleSearchValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    setErrorMessage(undefined);
    setSearchValue(event.target.value);
    setFilterId('');
  };

  const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      await handleSearch();
    }
  };

  const handleSearchMenuInTree = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleAddMenuClick();
    }
  };

  const handlePageTitle = () => {
    const str = activateOrExtract.toString().toLowerCase();
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  const pageTitle = handlePageTitle();
  

  return (
    <>
      <Header
        breadcrumb={{ to: '/extract', label: 'Back to Extract Queue' }}
        title={`${pageTitle} Menus`}
      />
      <MenuExtractPage>
        <TableRowWrapper>
          <TreeViewRowWrapper>
            <StyledHeader data-testid='header'>
              <HeaderWrapper>
                <StyledHeaderTitle>Available Menus</StyledHeaderTitle>
              </HeaderWrapper>
              <FiltersWrapper>
                <StyledSubTitle>Search by:</StyledSubTitle>
                <Radio
                  checked={radioFilter === 'marketNo'}
                  value='marketNo'
                  onChange={handleRadioValueChange}
                  id={'marketNo'}
                  label={'Franchisee'}
                />
                <Radio
                  checked={radioFilter === 'storeNo'}
                  value='storeNo'
                  onChange={handleRadioValueChange}
                  id={'storeNo'}
                  label={'Store'}
                />
                <Radio
                  checked={radioFilter === 'menuNo'}
                  value='menuNo'
                  onChange={handleRadioValueChange}
                  id={'menuNo'}
                  label={'Menu'}
                />
              <HeaderInputButton>
                <InputButton
                  buttonProps={{
                    label: 'Search',
                    onClick: handleSearch,
                    testId: 'SearchButton',
                    disabled: searchValue === '',
                  }}
                  inputProps={{
                    onChange: handleSearchValue,
                    id: 'searchFilters',
                    label: '',
                    value: searchValue,
                    disabled: radioFilter === '',
                    onKeyDown: handleKeyDown,
                    testId: 'FilterInput',
                    ref: filterRef,
                    errorText: errorMessage,
                    maxLength: 20,
                  }}
                />
              </HeaderInputButton>
            </FiltersWrapper>
            </StyledHeader>
            <StyledSubHeader data-testid='subtitle'>
              <div style={{ marginLeft: '48px' }}>
                <Checkbox
                  type='checkbox'
                  testId='selectAllCheckBox'
                  checked={selectedMenus.length > 0 && selectedMenus.length === displayedRowCount}
                  id={'select-all'}
                  aria-checked={selectedMenus.length > 0 && selectedMenus.length === displayedRowCount}
                  readOnly
                  indeterminate={selectedMenus.length > 0 && selectedMenus.length < displayedRowCount}
                  label={<StyledSubTitle>Organizations</StyledSubTitle>}
                  onClick={handleSelectAll}
                />
              </div>
            </StyledSubHeader>
            <MenuTreeView
              rows={availableOrgs} 
              dataStructure={dataStructure}
              checkedRows={checkedRows}
              expandOnCheck={expandOnCheck}
              expandedRows={expandedRows}
              filterId={filterId}
              selectedRows={selectedMenus}
              onRowClick={handleRowClick}
              onCheckboxClick={handleCheckboxClickInternal}
              defaultHandleCheckboxClick={handleCheckboxClick}
              radioFilter={translateForTree[radioFilter]}
              isLoading={isOrgLoading}
              externalTableUpdate={tableUpdate}
            />
            <StyledFooter>
              <StyledFooterText>
                {`${displayedRowCount === undefined ? 0 : displayedRowCount} ${
                  displayedRowCount === 1 ? 'record' : 'records'
                }`}
              </StyledFooterText>
            </StyledFooter>
          </TreeViewRowWrapper>
          <TableWrapper>
            <Table
              width='90%'
              showHeader
              headerTitle='Selected'
              columnDefs={columnDefs}
              rowData={selectedMenus}
              height={'calc(100vh - 266px)'}
              returnedRecords={selectedMenus.length}
              headerButtons={[
                {
                  buttonProps: {
                    label: 'Add Menu',
                    testId: 'AddMenuSearchButton',
                    onClick: handleAddMenuClick,
                  },
                  inputProps: {
                    label: '',
                    'aria-label': `add menu to ${activateOrExtract === MENU_FILTERS.EXTRACT ? 'extract' : 'activate'}`,
                    id: 'addMenuToExtract',
                    value: searchInputValue,
                    onChange: handleInputChange,
                    onKeyDown: handleSearchMenuInTree,
                    errorText: searchErrorMessage,
                    testId: 'searchMenuInput',
                    disabled: inputError ? true : false,
                  },
                },
              ]}
              onRowSelected={handleSelectedMenusChange}
              ref={selectedGridRef}
              rowSelection='multiple'
              suppressRowClickSelection={true}
              getRowId={getRowId}
            />
          </TableWrapper>
        </TableRowWrapper>
        <Buttons>
          <Button
            label={isLoading ? 'Loading' : pageTitle}
            disabled={selectedMenus.length === 0 || disableButton}
            testId={
              activateOrExtract === MENU_FILTERS.EXTRACT ? 'ExtractMenusSubmitButton' : 'ActivateMenusSubmitButton'
            }
            onClick={handleClick}
            loadingLabel={'Loading'}
          />
        </Buttons>

      </MenuExtractPage>
    </>
  );
};

export default MenuExtract;
